import React, { MouseEvent, ReactNode, useEffect, useState } from 'react'
import { styled } from '@mui/material/styles'
import {
  Alert,
  AlertTitle,
  Autocomplete,
  Badge,
  Box,
  Button,
  ButtonProps,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  MenuItemProps,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useTheme
} from '@mui/material'
import { useAuth, useDialog, usePolicy } from '../../hooks'
import { AlertStatus, EntityEventAction, EventType } from '../../enums'
import { Trans, useTranslation } from 'react-i18next'
import useMenu from '../../hooks/useMenu'
import {
  ActionRequest,
  Company,
  Device,
  GeoCoordinate,
  Notification,
  NotificationEntity,
  Project,
  ProjectMembership,
  PrometheusAlert,
  RuuviTag,
  User,
  UserAlertRule
} from '../../types'
import { humanizeTimestamp } from '../../utils/date'
import { addDays, addHours, addYears, formatDistanceToNow, subHours } from 'date-fns'
import {
  ArrowDropDown,
  Circle,
  Email,
  Info,
  NotificationsNone,
  Place,
  Warning
} from '@mui/icons-material'
import { Link, useNavigate } from 'react-router-dom'
import { deleteRequest, getRequests } from '../../services/requestService'
import { RequestStatus } from '../../enums/RequestStatus'
import { RequestableType } from '../../enums/RequestableType'
import useDismissedActionRequests from '../../hooks/useDismissedActionRequests'
import ActionRequestRejectButton from '../ActionRequestRejectButton'
import ActionRequestAcceptButton from '../ActionRequestAcceptButton'
import { ActionRequestAction } from '../../enums/ActionRequestAction'
import { RequesterType } from '../../enums/RequesterType'
import { useDispatch } from 'react-redux'
import { getDevice } from '../../services/deviceService'
import DeviceDetachButton from '../DeviceDetachButton'
import Map, { Marker } from 'react-map-gl'
import LoadingState from '../LoadingState'
import { grey } from '@mui/material/colors'
import { LoadingButton } from '@mui/lab'
import ProjectExtendButton from '../ProjectExtendButton'
import { deleteDeviceLocationChangeRequest } from '../../redux/slices/requests'
import {
  createSuccessNotification,
  createWarningNotification
} from '../../redux/slices/notifications'
import { createUserNotification } from '../../utils/createUserNotification'
import { RequestAccessErrors } from '../../enums/RequestAccessErrors'
import { getProject } from '../../services/projectService'
import { getUser, getUserAlertRules } from '../../services/userService'
import { getCompany } from '../../services/companyService'
import {
  isActionRequestClosed,
  isCompanyActionRequest,
  isDeviceActionRequest,
  isProjectActionRequest,
  isUserActionRequest
} from '../../utils/actionRequest'
import {
  isActionRequestNotification,
  isAlertNotification,
  isEntityNotification,
  isProjectMembershipNotification,
  isUserNotification,
  sortNotificationsByDate
} from '../../utils'
import { subscribeNotifications } from '../../subscribers'
import { getRuuviTag } from '../../services/ruuviTagService'
import countryList from '../../lists/countryList.json'
import eicList from '../../lists/eicList.json'
import { mapBoxAPIKey } from '../../utils/apiKey'
import { defaultLocation } from '../../utils/coordinates'
import 'mapbox-gl/dist/mapbox-gl.css'
import SelectCountryCodeButton from '../SelectCountryCodeButton'
import ActionRequestInfoList from '../ActionRequestInfoList'

const Indicator = styled(Badge)`
  .MuiBadge-badge {
    background: ${(props) => props.theme.header.indicator.background};
    color: ${(props) => props.theme.palette.common.white};
  }
`

interface ActionRequestNotificationMenuItemProps extends Omit<
  MenuItemProps,
  'children' | 'onClick'
> {
  actionRequest: ActionRequest
  onClick?: () => void
}

function ActionRequestNotificationMenuItem({
  actionRequest,
  ...props
}: ActionRequestNotificationMenuItemProps) {
  switch (actionRequest.requestableType) {
    case RequestableType.COMPANY:
      return <CompanyActionRequestNotificationMenuItem actionRequest={actionRequest} {...props} />
    case RequestableType.PROJECT:
      return <ProjectActionRequestNotificationMenuItem actionRequest={actionRequest} {...props} />
    case RequestableType.DEVICE:
      return <DeviceActionRequestNotificationMenuItem actionRequest={actionRequest} {...props} />
    default:
      return null
  }
}

function CompanyActionRequestNotificationMenuItem({
  actionRequest,
  ...props
}: ActionRequestNotificationMenuItemProps) {
  switch (actionRequest.requesterType) {
    case RequesterType.USER:
      return (
        <RoleUpgradeActionRequestNotificationMenuItem actionRequest={actionRequest} {...props} />
      )
    default:
      return <></>
  }
}

function ProjectActionRequestNotificationMenuItem({
  actionRequest,
  ...props
}: ActionRequestNotificationMenuItemProps) {
  switch (actionRequest.action) {
    case ActionRequestAction.SELECT_COORDINATES:
      return (
        <ProjectSelectCoordinatesActionRequestNotificationMenuItem
          actionRequest={actionRequest}
          {...props}
        />
      )
    case ActionRequestAction.SELECT_EIC_AREA_CODE:
      return (
        <ProjectSelectEicActionRequestNotificationMenuItem
          actionRequest={actionRequest}
          {...props}
        />
      )
    case ActionRequestAction.SELECT_COUNTRY_CODE:
      return (
        <ProjectSelectCountryCodeActionRequestNotificationMenuItem
          actionRequest={actionRequest}
          {...props}
        />
      )
    case null:
      // If the action is null, we need to handle it based on the requester type.
      // TODO: These should contain an action request type.
      break
    default:
      console.warn(`Unknown action request action: ${actionRequest.action}`)
  }
  switch (actionRequest.requesterType) {
    case RequesterType.USER:
      return (
        <ProjectMembershipActionRequestNotificationMenuItem
          actionRequest={actionRequest}
          {...props}
        />
      )
    case RequesterType.DEVICE:
      return (
        <DeviceLocationChangedActionRequestNotificationMenuItem
          actionRequest={actionRequest}
          {...props}
        />
      )
    case RequesterType.SYSTEM:
      return (
        <ProjectEndedActionRequestNotificationMenuItem actionRequest={actionRequest} {...props} />
      )
    default:
      return <></>
  }
}

function DeviceActionRequestNotificationMenuItem({
  actionRequest,
  ...props
}: ActionRequestNotificationMenuItemProps) {
  switch (actionRequest.action) {
    case ActionRequestAction.ENABLE_ALERT_NOTIFICATIONS:
      return (
        <EnableAlertNotificationsActionRequestNotificationMenuItem
          actionRequest={actionRequest}
          {...props}
        />
      )
    default:
      return <></>
  }
}

function RoleUpgradeActionRequestNotificationMenuItem({
  actionRequest,
  onClick,
  ...props
}: ActionRequestNotificationMenuItemProps) {
  /**
   * The translate function.
   */
  const { t } = useTranslation('common')

  /**
   * The dialog state.
   */
  const { open, openDialog, closeDialog } = useDialog()

  /**
   * The user state.
   */
  const [user, setUser] = useState<User>(actionRequest?.requester as User)

  /**
   * The company state.
   */
  const [company, setCompany] = useState<Company>(actionRequest?.requestable as Company)

  /**
   * The title.
   */
  const title = t('requestFromUser')

  /**
   * The message.
   */
  const message = t('roleRequested', {
    user: user
      ? `${user?.firstName} ${user?.lastName}`
      : `${t('user')} #{actionRequest.requesterId}}`,
    role: actionRequest?.role ?? 'Admin',
    company: company
      ? company?.name
      : `${t('company').toLowerCase()} #${actionRequest.requestableId}`
  })

  /**
   * The timestamp.
   */
  const timestamp = new Date(actionRequest.createdAt)

  /**
   * Handle the "close" action.
   */
  const handleClose = () => {
    closeDialog()

    onClick?.()
  }

  /**
   * Handle "after submit" action.
   */
  const handleAfterSubmit = () => {
    closeDialog()

    onClick?.()
  }

  /**
   * Load the user.
   *
   * @param id The user ID
   */
  const loadUser = async (id: number) => {
    setUser(await getUser(id))
  }

  /**
   * Load the company.
   */
  const loadCompany = async (id: number) => {
    setCompany(await getCompany(id))
  }

  /**
   * Load the user and company unless already included in the action request.
   */
  useEffect(() => {
    if (actionRequest?.requester === undefined && actionRequest?.requesterId !== null) {
      loadUser(parseInt(actionRequest.requesterId))
    }

    if (actionRequest?.requestable === undefined) {
      loadCompany(parseInt(actionRequest.requestableId))
    }
  }, [actionRequest])

  return (
    <>
      <MessageMenuItem
        title={title}
        message={message}
        timestamp={timestamp}
        icon={<Email />}
        button={
          <Stack direction="row" alignItems="center" gap={1}>
            <ActionRequestRejectButton
              variant="text"
              color="error"
              size="small"
              actionRequest={actionRequest}
              afterSubmit={handleAfterSubmit}
            />

            <ActionRequestAcceptButton
              variant="text"
              color="success"
              size="small"
              actionRequest={actionRequest}
              afterSubmit={handleAfterSubmit}
            />
          </Stack>
        }
        {...props}
        onClick={openDialog}
      />

      <Dialog open={open} maxWidth="md" fullWidth onClose={handleClose}>
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            {title}
            <Typography color="disabled">{humanizeTimestamp(timestamp)}</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Alert severity="info">{message}</Alert>
          <ActionRequestInfoList actionRequest={actionRequest} sx={{ mt: 1 }} />
        </DialogContent>
        <DialogActions>
          <Button variant="text" color="inherit" onClick={handleClose}>
            {t('cancel')}
          </Button>

          <ActionRequestRejectButton
            variant="contained"
            color="error"
            actionRequest={actionRequest}
            afterSubmit={handleAfterSubmit}
          />

          <ActionRequestAcceptButton
            variant="contained"
            color="success"
            actionRequest={actionRequest}
            afterSubmit={handleAfterSubmit}
          />
        </DialogActions>
      </Dialog>
    </>
  )
}

function DeviceLocationChangedActionRequestNotificationMenuItem({
  actionRequest,
  onClick,
  ...props
}: ActionRequestNotificationMenuItemProps) {
  /**
   * The translate function.
   */
  const { t } = useTranslation('common')

  /**
   * The dialog state.
   */
  const { open, openDialog, closeDialog } = useDialog()

  /**
   * The dispatch function.
   */
  const dispatch = useDispatch()

  /**
   * The current user.
   */
  const { currentUser } = useAuth()

  /**
   * The device.
   */
  const [device, setDevice] = useState<Device>()

  /**
   * Indicates if the device is being loaded.
   */
  const [loading, setLoading] = useState<boolean>(false)

  /**
   * Indicates if the action request is being deleted.
   */
  const [isDeleting, setIsDeleting] = useState<boolean>(false)

  /**
   * The dismissed action requests.
   */
  const dismissedActionRequests = useDismissedActionRequests()

  /**
   * The theme.
   */
  const theme = useTheme()

  /**
   * The current location of the device.
   */
  const currentLocation: GeoCoordinate | null = device?.locationHistory?.[0]
    ? {
        lat: parseFloat(device.locationHistory[0].lat.toString()),
        long: parseFloat(device.locationHistory[0].lng.toString())
      }
    : null

  /**
   * The previous location of the device.
   */
  const previousLocation: GeoCoordinate | null = device?.locationHistory?.[1]
    ? {
        lat: parseFloat(device.locationHistory[1].lat.toString()),
        long: parseFloat(device.locationHistory[1].lng.toString())
      }
    : null

  /**
   * Load the device.
   */
  const loadDevice = async () => {
    try {
      setLoading(true)

      if (actionRequest.requesterId !== null) {
        setDevice(await getDevice(actionRequest.requesterId))
      }

      setLoading(false)
    } catch (_error) {
      // Do nothing.
      setLoading(false)
    }
  }

  /**
   * Handle the "dismiss" action.
   */
  const handleDismiss = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation()

    if (currentUser) {
      const dismissedRequests = [...dismissedActionRequests]

      if (!dismissedRequests.includes(actionRequest.id)) {
        dismissedRequests.push(actionRequest.id)
      }

      window.localStorage.setItem(
        'dismissedActionRequests',
        JSON.stringify({
          [currentUser.id]: dismissedRequests
        })
      )
    }

    onClick?.()
  }

  /**
   * Handle the "delete" action.
   */
  async function handleDelete() {
    try {
      setIsDeleting(true)

      await deleteRequest(actionRequest.id)
      dispatch(deleteDeviceLocationChangeRequest(actionRequest.id))

      dispatch(createSuccessNotification(t('messageDeleteSuccess')))

      setIsDeleting(false)

      onClick?.()
    } catch (error) {
      setIsDeleting(false)

      const errorMessage = createUserNotification({
        user: currentUser,
        type: RequestAccessErrors.DELETE,
        error: error
      })

      dispatch(createWarningNotification(t(errorMessage?.key) + t(errorMessage?.message)))
    }
  }

  /**
   * Handle the "open" action.
   */
  const handleOpen = () => {
    loadDevice()
    openDialog()
  }

  /**
   * Handle "close" action.
   */
  const handleClose = () => {
    closeDialog()

    onClick?.()
  }

  return (
    <>
      <MessageMenuItem
        title={t('deviceLocationChangeNotificationTitle')}
        message={t('deviceLocationChangeNotificationMessage', {
          device: actionRequest.requesterId
        })}
        timestamp={new Date(actionRequest.createdAt)}
        icon={<Place />}
        button={
          <Stack direction="row" justifyContent="flex-start" alignItems="center" gap={1}>
            <Button variant="text" color="primary" size="small" onClick={handleOpen}>
              {t('showLocation')}
            </Button>

            <Button variant="text" color="inherit" size="small" onClick={handleDismiss}>
              {t('hide')}
            </Button>
          </Stack>
        }
        {...props}
        onClick={handleOpen}
      />

      <Dialog open={open} maxWidth="md" fullWidth onClose={handleClose}>
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            {t('deviceLocationChangeNotificationTitle')}
            <Typography>{humanizeTimestamp(actionRequest.createdAt)}</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 3 }}>
            {t('deviceLocationChangeNotificationMessage', {
              device: actionRequest.requesterId
            })}
          </Alert>

          {loading ? (
            <LoadingState />
          ) : (
            currentLocation && (
              <Map
                mapboxAccessToken={mapBoxAPIKey}
                initialViewState={{
                  latitude: currentLocation.lat,
                  longitude: currentLocation.long,
                  zoom: 10
                }}
                mapStyle="mapbox://styles/mapbox/streets-v9"
                style={{ width: '100%', height: 560 }}
              >
                {previousLocation && (
                  <Marker
                    color={grey[400]}
                    longitude={previousLocation.long}
                    latitude={previousLocation.lat}
                  />
                )}

                <Marker
                  color={theme.palette.success.light}
                  longitude={currentLocation.long}
                  latitude={currentLocation.lat}
                />
              </Map>
            )
          )}

          {device && (
            <>
              <List>
                {device?.asset?.name && device.asset.name !== device.name && (
                  <ListItem>
                    <ListItemText primary={device.asset.name} secondary={t('name')} />
                  </ListItem>
                )}

                <ListItem key="device-code">
                  <ListItemText primary={device.name} secondary={t('deviceCode')} />
                </ListItem>

                <ListItem key="short-uuid">
                  <ListItemText primary={device.shortUUID} secondary={t('shortUUID')} />
                </ListItem>

                {currentLocation && (
                  <ListItem key="current-location">
                    <ListItemText
                      primary={`${currentLocation.lat}, ${currentLocation.long}`}
                      secondary={t('currentLocation')}
                    />
                  </ListItem>
                )}

                {device?.asset?.company && (
                  <ListItem key="company">
                    <ListItemText primary={device.asset.company.name} secondary={t('company')} />
                  </ListItem>
                )}

                {device?.asset?.project && (
                  <ListItem key="project">
                    <ListItemText primary={device.asset.project.name} secondary={t('project')} />
                  </ListItem>
                )}
              </List>
            </>
          )}

          {device && (
            <Alert
              severity="warning"
              icon={false}
              action={
                <Stack direction="row" gap={1}>
                  <Button variant="text" color="inherit" size="small" onClick={handleClose}>
                    {t('iDoNot')}
                  </Button>
                  <DeviceDetachButton
                    device={device}
                    startIcon={false}
                    variant="outlined"
                    label={t('yes')}
                    color="warning"
                    size="small"
                  />
                </Stack>
              }
            >
              {t('doYouWantToDetachThisDeviceFromProject')}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button variant="text" color="inherit" onClick={handleClose}>
            {t('cancel')}
          </Button>

          <LoadingButton variant="text" color="error" loading={isDeleting} onClick={handleDelete}>
            {t('deleteMessage')}
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </>
  )
}

interface MapClickEvent {
  lngLat: {
    lng: number
    lat: number
  }
}

function ProjectSelectCoordinatesActionRequestNotificationMenuItem({
  actionRequest,
  onClick,
  ...props
}: ActionRequestNotificationMenuItemProps) {
  const { t } = useTranslation()

  const [project, setProject] = useState<Project | undefined>()
  const [viewport, setViewport] = useState({
    longitude: defaultLocation.long,
    latitude: defaultLocation.lat,
    zoom: 5
  })

  const { open, openDialog, closeDialog } = useDialog()
  const [selectedLocation, setSelectedLocation] = useState<GeoCoordinate | undefined>(undefined)

  const handleAfterSubmit = () => {
    closeDialog()

    onClick?.()
  }

  const handleMapClick = (event: MapClickEvent) => {
    const { lat, lng } = event.lngLat

    setSelectedLocation({
      lat,
      long: lng
    })
  }

  /**
   * Load the project.
   *
   * @param id The project ID
   */
  const loadProject = async (id: number) => {
    setProject(await getProject(id))
  }

  useEffect(() => {
    loadProject(parseInt(actionRequest.requestableId))
  }, [actionRequest])

  const title = t('SelectCoordinates.title', { ns: 'actionRequest' })
  const message = t('SelectCoordinates.message', {
    ns: 'actionRequest',
    projectName: project?.name ?? 'Unknown project'
  })
  const timestamp = new Date(actionRequest.createdAt)

  return (
    <>
      <MessageMenuItem
        title={title}
        message={message}
        timestamp={timestamp}
        icon={<Email />}
        {...props}
        onClick={openDialog}
        button={
          <Stack direction="row" alignItems="center" gap={1}>
            <Button>{t('SelectCoordinates.button', { ns: 'actionRequest' })}</Button>
          </Stack>
        }
      />
      <Dialog open={open} maxWidth="md" fullWidth onClose={closeDialog}>
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            {title}
            <Typography color="disabled">{humanizeTimestamp(timestamp)}</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Alert severity="info">
            {t('SelectCoordinates.info', {
              projectName: project?.name ?? 'Unknown project',
              ns: 'actionRequest'
            })}
          </Alert>
          <ActionRequestInfoList actionRequest={actionRequest} sx={{ mt: 1 }} />
          <Box sx={{ mt: 2, height: 400 }}>
            <Map
              {...viewport}
              mapboxAccessToken={mapBoxAPIKey}
              onMove={(evt) => setViewport(evt.viewState)}
              onClick={handleMapClick}
              mapStyle="mapbox://styles/mapbox/streets-v12"
              style={{ width: '100%', height: '100%' }}
            >
              {selectedLocation && (
                <Marker
                  longitude={selectedLocation.long}
                  latitude={selectedLocation.lat}
                  anchor="bottom"
                >
                  <div
                    style={{
                      width: 20,
                      height: 20,
                      backgroundColor: '#ff0000',
                      borderRadius: '50%',
                      border: '2px solid white',
                      cursor: 'pointer'
                    }}
                  />
                </Marker>
              )}
            </Map>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button variant="text" color="inherit" onClick={closeDialog}>
            {t('cancel')}
          </Button>

          <ActionRequestAcceptButton
            variant="contained"
            color="success"
            actionRequest={actionRequest}
            afterSubmit={handleAfterSubmit}
            submitInput={{
              coordinates: selectedLocation
            }}
            disabled={!selectedLocation}
          />
        </DialogActions>
      </Dialog>
    </>
  )
}

function ProjectSelectEicActionRequestNotificationMenuItem({
  actionRequest,
  onClick,
  ...props
}: ActionRequestNotificationMenuItemProps) {
  const { t } = useTranslation()

  const [project, setProject] = useState<Project | undefined>()

  const { open, openDialog, closeDialog } = useDialog()
  const [selectedEIC, setSelectedEIC] = useState<string | undefined>(undefined)

  const handleAfterSubmit = () => {
    closeDialog()

    onClick?.()
  }

  /**
   * Load the project.
   *
   * @param id The project ID
   */
  const loadProject = async (id: number) => {
    setProject(await getProject(id))
  }

  useEffect(() => {
    loadProject(parseInt(actionRequest.requestableId))
  }, [actionRequest])

  const title = t('SelectEic.title', { ns: 'actionRequest' })
  const message = t('SelectEic.message', {
    projectName: project?.name ?? 'Unknown project',
    ns: 'actionRequest'
  })
  const timestamp = new Date(actionRequest.createdAt)

  return (
    <>
      <MessageMenuItem
        title={title}
        message={message}
        timestamp={timestamp}
        icon={<Email />}
        {...props}
        onClick={openDialog}
        button={
          <Stack direction="row" alignItems="center" gap={1}>
            <Button>{t('SelectEic.button', { ns: 'actionRequest' })}</Button>
          </Stack>
        }
      />
      <Dialog open={open} maxWidth="md" fullWidth onClose={closeDialog}>
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            {title}
            <Typography color="disabled">{humanizeTimestamp(timestamp)}</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Alert severity="info">
            <Trans
              t={useTranslation('actionRequest').t}
              ns="actionRequest"
              i18nKey="SelectEic.info"
              values={{
                projectName: project?.name ?? 'Unknown project'
              }}
              components={[
                <Link to="https://www.entsoe.eu/data/energy-identification-codes-eic/eic-area-codes-map/" />
              ]}
            />
          </Alert>
          <ActionRequestInfoList actionRequest={actionRequest} sx={{ mt: 1 }} />
          <Box sx={{ mt: 2, mb: 2 }}>
            <Autocomplete
              options={eicList.areas.map((option) => option.eic)}
              autoHighlight
              getOptionLabel={(option) => option}
              onChange={(_, value) => {
                setSelectedEIC(value ?? undefined)
              }}
              renderInput={(params) => <TextField {...params} label={t('eicCode')} />}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button variant="text" color="inherit" onClick={closeDialog}>
            {t('cancel')}
          </Button>

          <ActionRequestAcceptButton
            variant="contained"
            color="success"
            actionRequest={actionRequest}
            afterSubmit={handleAfterSubmit}
            submitInput={{
              eic: selectedEIC
            }}
            disabled={!selectedEIC}
          />
        </DialogActions>
      </Dialog>
    </>
  )
}

function ProjectSelectCountryCodeActionRequestNotificationMenuItem({
  actionRequest,
  onClick,
  ...props
}: ActionRequestNotificationMenuItemProps) {
  const { t } = useTranslation()

  const [project, setProject] = useState<Project | undefined>()

  const handleAfterSubmit = () => {
    onClick?.()
  }

  /**
   * Load the project.
   *
   * @param id The project ID
   */
  const loadProject = async (id: number) => {
    setProject(await getProject(id))
  }

  useEffect(() => {
    loadProject(parseInt(actionRequest.requestableId))
  }, [actionRequest])

  const title = t('SelectCountryCode.title', { ns: 'actionRequest' })
  const message = t('SelectCountryCode.message', {
    projectName: project?.name ?? 'Unknown project',
    ns: 'actionRequest'
  })
  const timestamp = new Date(actionRequest.createdAt)

  return project ? (
    <>
      <MessageMenuItem
        title={title}
        message={message}
        timestamp={timestamp}
        icon={<Email />}
        {...props}
        button={
          <Stack direction="row" alignItems="center" gap={1}>
            <SelectCountryCodeButton
              actionRequest={actionRequest}
              project={project}
              handleAfterSubmit={handleAfterSubmit}
            />
          </Stack>
        }
      />
    </>
  ) : null
}

function ProjectMembershipActionRequestNotificationMenuItem({
  actionRequest,
  onClick,
  ...props
}: ActionRequestNotificationMenuItemProps) {
  /**
   * The translate function.
   */
  const { t } = useTranslation('common')

  /**
   * The dialog state.
   */
  const { open, openDialog, closeDialog } = useDialog()

  /**
   * The user state.
   */
  const [user, setUser] = useState<User>(actionRequest?.requester as User)

  /**
   * The project state.
   */
  const [project, setProject] = useState<Project>(actionRequest?.requestable as Project)

  /**
   * The title.
   */
  const title = t('requestFromUser')

  /**
   * Resolve the message.
   */
  const resolveMessage = () => {
    return t('projectMembershipRequested', {
      user: user
        ? `${user.firstName} ${user.lastName}`
        : `${t('user')} #${actionRequest.requesterId}`,
      project: project ? project.name : `${t('project')} #${actionRequest.requestableId}`
    })
  }

  /**
   * The message.
   */
  const message = resolveMessage()

  /**
   * The timestamp.
   */
  const timestamp = new Date(actionRequest.createdAt)

  /**
   * Handle the "close" action.
   */
  const handleClose = () => {
    closeDialog()

    onClick?.()
  }

  /**
   * Handle "after submit" action.
   */
  const handleAfterSubmit = () => {
    closeDialog()

    onClick?.()
  }

  /**
   * Load the user.
   *
   * @param id The user ID
   */
  const loadUser = async (id: number) => {
    setUser(await getUser(id))
  }

  /**
   * Load the project.
   *
   * @param id The project ID
   */
  const loadProject = async (id: number) => {
    setProject(await getProject(id))
  }

  /**
   * Load the user and project unless already included in the action request.
   */
  useEffect(() => {
    if (actionRequest?.requester === undefined && actionRequest.requesterId !== null) {
      loadUser(parseInt(actionRequest.requesterId))
    }

    if (actionRequest?.requestable === undefined) {
      loadProject(parseInt(actionRequest.requestableId))
    }
  }, [actionRequest])

  return (
    <>
      <MessageMenuItem
        title={title}
        message={message}
        timestamp={timestamp}
        icon={<Email />}
        button={
          <Stack direction="row" alignItems="center" gap={1}>
            <ActionRequestRejectButton
              variant="text"
              color="error"
              size="small"
              actionRequest={actionRequest}
              afterSubmit={handleAfterSubmit}
            />

            <ActionRequestAcceptButton
              variant="text"
              color="success"
              size="small"
              actionRequest={actionRequest}
              afterSubmit={handleAfterSubmit}
            />
          </Stack>
        }
        {...props}
        onClick={openDialog}
      />

      <Dialog open={open} maxWidth="md" fullWidth onClose={handleClose}>
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            {title}
            <Typography color="disabled">{humanizeTimestamp(timestamp)}</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Alert severity="info">{message}</Alert>
          <ActionRequestInfoList actionRequest={actionRequest} sx={{ mt: 1 }} />
        </DialogContent>
        <DialogActions>
          <Button variant="text" color="inherit" onClick={handleClose}>
            {t('cancel')}
          </Button>

          <ActionRequestRejectButton
            variant="contained"
            color="error"
            actionRequest={actionRequest}
            afterSubmit={handleAfterSubmit}
          />

          <ActionRequestAcceptButton
            variant="contained"
            color="success"
            actionRequest={actionRequest}
            afterSubmit={handleAfterSubmit}
          />
        </DialogActions>
      </Dialog>
    </>
  )
}

function ProjectEndedActionRequestNotificationMenuItem({
  actionRequest,
  onClick,
  ...props
}: ActionRequestNotificationMenuItemProps) {
  /**
   * The translate function.
   */
  const { t } = useTranslation('common')

  /**
   * The dialog context.
   */
  const { open, openDialog, closeDialog } = useDialog()

  /**
   * The project state.
   */
  const [project, setProject] = useState(
    isProjectActionRequest(actionRequest) ? actionRequest.requestable : undefined
  )

  /**
   * The end date of the project.
   */
  const endDate = project?.endDate ? new Date(project?.endDate) : undefined

  /**
   * The title
   */
  const title = t('actionRequest')

  /**
   * The message.
   */
  const message =
    project == undefined
      ? t('projectHasEnded')
      : t('projectHasEndedTimeAgo', {
          project: project.name,
          ago: endDate ? formatDistanceToNow(endDate, { addSuffix: true }) : undefined
        })

  /**
   * The timestamp.
   */
  const timestamp = new Date(actionRequest.createdAt)

  /**
   * Handle the event after "accept".
   */
  const handleAfterAccept = () => {
    onClick?.()
  }

  /**
   * Handle the click event to "extend" aciton.
   */
  const handleExtendClick = () => {
    closeDialog()
  }

  /**
   * Handle the event after the "extend" action.
   */
  const handleAfterExtend = () => {
    closeDialog()
    onClick?.()
  }

  /**
   * Load the project.
   */
  const loadProject = async (id: number) => {
    setProject(await getProject(id))
  }

  /**
   * Load the project unless it is already included in the action request.
   */
  useEffect(() => {
    if (actionRequest?.requestable === undefined) {
      loadProject(parseInt(actionRequest.requestableId))
    }
  }, [actionRequest])

  return (
    <>
      <MessageMenuItem
        title={title}
        message={message}
        timestamp={timestamp}
        icon={<Info />}
        button={
          <Stack direction="row" alignItems="center" gap={1}>
            {project && (
              <>
                <ProjectExtendButton
                  variant="text"
                  color="inherit"
                  size="small"
                  project={project}
                  actionRequest={actionRequest}
                  onClick={handleExtendClick}
                  afterSubmit={handleAfterExtend}
                />

                <ActionRequestAcceptButton
                  variant="text"
                  color="inherit"
                  size="small"
                  label={t('endProject')}
                  actionRequest={actionRequest}
                  afterSubmit={handleAfterAccept}
                />
              </>
            )}
          </Stack>
        }
        {...props}
        onClick={openDialog}
      />

      <Dialog open={open} maxWidth="sm" fullWidth keepMounted onClose={closeDialog}>
        <DialogTitle>{title}</DialogTitle>
        <DialogContent>
          <Alert severity="info">{message}</Alert>
        </DialogContent>
        <DialogActions>
          <Button variant="text" color="inherit" onClick={closeDialog}>
            {t('cancel')}
          </Button>

          {project && (
            <ProjectExtendButton
              variant="text"
              color="inherit"
              project={project}
              actionRequest={actionRequest}
              label={t('extendProject')}
              onClick={handleExtendClick}
              afterSubmit={handleAfterExtend}
            />
          )}

          <ActionRequestAcceptButton
            variant="text"
            color="inherit"
            label={t('endProject')}
            actionRequest={actionRequest}
            afterSubmit={handleAfterAccept}
          />
        </DialogActions>
      </Dialog>
    </>
  )
}

function EnableAlertNotificationsActionRequestNotificationMenuItem({
  actionRequest,
  onClick,
  ...props
}: ActionRequestNotificationMenuItemProps) {
  /**
   * The translate function.
   */
  const { t } = useTranslation(['common', 'actionRequest'])

  /**
   * The dialog state.
   */
  const { open, openDialog, closeDialog } = useDialog()

  /**
   * The navigate function.
   */
  const navigate = useNavigate()

  /**
   * The device state.
   */
  const [device, setDevice] = useState<Device>(actionRequest?.requestable as Device)

  /**
   * The title.
   */
  const title = t('actionRequest')

  /**
   * The message.
   */
  const message = t('EnableAlertNotifications.title', {
    ns: 'actionRequest',
    device: device?.asset?.name || device?.name || actionRequest.requestableId
  })

  /**
   * The timestamp.
   */
  const timestamp = new Date(actionRequest.createdAt)

  /**
   * Handle the event after "enable" action.
   */
  const handleAfterEnable = () => {
    closeDialog()
    onClick?.()
  }

  /**
   * Handle the "showAlerts" action.
   */
  const handleShowAlerts = () => {
    navigate(`/devices/${actionRequest.requestableId}/alerts`)
    closeDialog()
    onClick?.()
  }

  /**
   * Load the device.
   */
  const loadDevice = async (name: string) => {
    setDevice(await getDevice(name))
  }

  /**
   * Load the device unless it is already included in the action request.
   */
  useEffect(() => {
    if (actionRequest.requestable === undefined) {
      loadDevice(actionRequest.requestableId)
    }
  }, [actionRequest])

  return (
    <>
      <MessageMenuItem
        title={title}
        message={message}
        timestamp={timestamp}
        icon={<NotificationsNone />}
        button={
          <Stack direction="row" alignItems="center" gap={1}>
            <ActionRequestAcceptButton
              variant="contained"
              color="primary"
              size="small"
              label={t('enable')}
              actionRequest={actionRequest}
              afterSubmit={handleAfterEnable}
            />

            <Button variant="text" color="primary" size="small" onClick={handleShowAlerts}>
              {t('showAlerts')}
            </Button>
          </Stack>
        }
        {...props}
        onClick={openDialog}
      />

      <Dialog open={open} maxWidth="sm" fullWidth onClose={closeDialog}>
        <DialogTitle>{title}</DialogTitle>
        <DialogContent>
          <Alert severity="info">
            <AlertTitle>
              {t('EnableAlertNotifications.title', {
                device: device?.asset?.name || device?.name || name,
                ns: 'actionRequest'
              })}
            </AlertTitle>

            <Typography>
              {t('EnableAlertNotifications.message', {
                ns: 'actionRequest'
              })}
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button variant="text" color="inherit" onClick={closeDialog}>
            {t('cancel')}
          </Button>

          <Button variant="text" color="primary" size="small" onClick={handleShowAlerts}>
            {t('showAlerts')}
          </Button>

          <ActionRequestAcceptButton
            variant="contained"
            color="primary"
            label={t('EnableAlertNotifications.action', {
              ns: 'actionRequest'
            })}
            actionRequest={actionRequest}
            afterSubmit={handleAfterEnable}
          />
        </DialogActions>
      </Dialog>
    </>
  )
}
interface MessageMenuItemProps extends Omit<MenuItemProps, 'children'> {
  title?: string
  message?: string
  timestamp?: Date
  icon?: ReactNode
  button?: ReactNode
  navigationId?: number | string
  requestId?: string
}

function MessageMenuItem({
  title,
  message,
  timestamp,
  icon,
  button,
  navigationId: _navigationId,
  requestId: _requestId,
  ...props
}: MessageMenuItemProps) {
  return (
    <MenuItem {...props} sx={{ py: 2 }}>
      {icon && <ListItemIcon sx={{ alignSelf: 'flex-start' }}>{icon}</ListItemIcon>}

      <ListItemText
        primaryTypographyProps={{ component: 'div' }}
        sx={{
          pr: '0 !important',
          textWrap: 'nowrap !important',
          textOverflow: 'ellipsis !important',
          overflow: 'hidden !important'
        }}
        primary={
          <Stack direction="column" gap={1}>
            <Typography
              title={message}
              component="span"
              sx={{
                textWrap: 'nowrap !important',
                textOverflow: 'ellipsis !important',
                overflow: 'hidden !important'
              }}
            >
              {message}
            </Typography>
            {button}
          </Stack>
        }
        secondary={
          <Stack direction="row" alignItems="center" justifyContent="start" gap={2}>
            {title && (
              <>
                <Typography variant="subtitle2" component="span">
                  {title}
                </Typography>
                <Circle sx={{ fontSize: '6px' }} />
              </>
            )}

            {timestamp && (
              <Tooltip title={humanizeTimestamp(timestamp)}>
                <Typography component="span">
                  {formatDistanceToNow(timestamp, { addSuffix: true })}
                </Typography>
              </Tooltip>
            )}
          </Stack>
        }
        secondaryTypographyProps={{
          component: 'div',
          sx: {
            textWrap: 'nowrap',
            textOverflow: 'ellipsis',
            overflow: 'hidden'
          }
        }}
      />
    </MenuItem>
  )
}

interface MfaNotificationProps extends Omit<MenuItemProps, 'children' | 'onClick'> {
  onClick?: (event?: MouseEvent<HTMLLIElement>) => void
}

function MfaNotification({ onClick, ...props }: MfaNotificationProps) {
  /**
   * The translate function.
   */
  const { t } = useTranslation('common')

  /**
   * The navigate function.
   */
  const navigate = useNavigate()

  /**
   * Handle the "enable" action.
   */
  const handleEnable = (event: MouseEvent<HTMLLIElement | HTMLButtonElement>) => {
    event.stopPropagation()

    navigate('/mfa')

    onClick?.()
  }

  /**
   * Handle the dismiss event.
   */
  const handleDismiss = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation()

    window.localStorage.setItem('dismissMfaNotification', JSON.stringify(true))

    onClick?.()
  }

  return (
    <MessageMenuItem
      title={t('security')}
      message={t('enableMfaNotification')}
      timestamp={new Date()}
      icon={<Warning color="warning" />}
      button={
        <Stack direction="row" alignItems="center" gap={1}>
          <Button variant="contained" color="primary" size="small" onClick={handleEnable}>
            {t('enable')}
          </Button>
          <Button variant="text" color="inherit" size="small" onClick={handleDismiss}>
            {t('hide')}
          </Button>
        </Stack>
      }
      onClick={handleEnable}
      {...props}
    />
  )
}

interface VatNotificationProps extends Omit<MenuItemProps, 'children' | 'onClick'> {
  onClick?: (event?: MouseEvent<HTMLLIElement>) => void
}

function VatNotification({ onClick, ...props }: VatNotificationProps) {
  /**
   * The translate function.
   */
  const { t } = useTranslation('common')

  /**
   * The navigate function.
   */
  const navigate = useNavigate()

  /**
   * The current user.
   */
  const { currentUser } = useAuth()

  /**
   * Handle the click event.
   */
  const handleClick = (event: MouseEvent<HTMLLIElement>) => {
    navigate(`/companies/${currentUser?.company?.id}`)
    onClick?.(event)
  }

  /**
   * Handle the dismiss action.
   */
  const handleDismiss = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation()

    window.localStorage.setItem('dismissVatNotification', JSON.stringify(true))
    onClick?.()
  }

  /**
   * Handle the "add VAT number" action.
   */
  const handleAddVatNumber = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation()

    navigate(`/companies/${currentUser?.company?.id}`)
    onClick?.()
  }

  return (
    <MessageMenuItem
      title={t('administration')}
      message={t('addVatNumberTitle')}
      timestamp={new Date()}
      icon={<Warning color="warning" />}
      button={
        <Stack direction="row" alignItems="center" gap={1}>
          <Button variant="contained" color="primary" size="small" onClick={handleAddVatNumber}>
            {t('addVatNumber')}
          </Button>
          <Button variant="text" color="inherit" size="small" onClick={handleDismiss}>
            {t('hide')}
          </Button>
        </Stack>
      }
      {...props}
      onClick={handleClick}
    />
  )
}

type MuteInterval = '1h' | '1d'

interface MuteButtonProps extends Omit<ButtonProps, 'onClick' | 'children'> {
  options?: MuteInterval[]
  onMute: (interval?: MuteInterval) => void
}

function MuteButton({ options = ['1h', '1d'], onMute, ...props }: MuteButtonProps) {
  /**
   * The translate function.
   */
  const { t } = useTranslation('common')

  /**
   * The menu context.
   */
  const { isMenuOpen, anchorEl, openMenu, closeMenu } = useMenu()

  /**
   * Handle the click event.
   */
  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    if (options) {
      openMenu(event)
    } else {
      onMute()
    }
  }

  /**
   * Parse the label.
   */
  const parseLabel = (option: MuteInterval) => {
    switch (option) {
      case '1h':
        return t('forOneHour')
      case '1d':
        return t('forOneDay')
      default:
        return t('permanently')
    }
  }

  /**
   * Handle the "mute" event.
   */
  const handleMute = (interval?: MuteInterval) => {
    onMute(interval)
    closeMenu()
  }

  return (
    <>
      <Button
        variant="outlined"
        size="small"
        endIcon={options.length > 0 ? <ArrowDropDown /> : undefined}
        sx={{
          '& .MuiButton-endIcon': {
            marginLeft: 1
          }
        }}
        {...props}
        onClick={handleClick}
      >
        {t('mute')}
      </Button>

      <Menu open={isMenuOpen} anchorEl={anchorEl} onClose={closeMenu}>
        {options?.map((option) => (
          <MenuItem key={option} onClick={() => handleMute(option)}>
            {parseLabel(option)}
          </MenuItem>
        ))}
      </Menu>
    </>
  )
}

interface NotificationMenuItemTextPrimaryProps {
  title?: string | JSX.Element
  action?: string | JSX.Element
}

function NotificationMenuItemTextPrimary({ title, action }: NotificationMenuItemTextPrimaryProps) {
  return (
    <Stack direction="column" gap={2}>
      {typeof title === 'string' ? <Typography variant="subtitle2">{title}</Typography> : title}
      {action}
    </Stack>
  )
}

interface NotificationMenuItemTextSecondaryProps {
  notification: Notification<NotificationEntity | PrometheusAlert>
  title?: string | JSX.Element
}

function NotificationMenuItemTextSecondary({
  notification,
  title
}: NotificationMenuItemTextSecondaryProps) {
  /**
   * The translate function.
   */
  const { t } = useTranslation('common')

  /**
   * The default title.
   */
  const defaultTitle = isAlertNotification(notification) ? t('alert') : t('event')

  /**
   * The timestamp as date.
   */
  const timestamp = new Date(notification.timestamp)

  return (
    <Stack direction="row" alignItems="center" justifyContent="start" gap={2}>
      <Typography variant="subtitle2">{title ?? defaultTitle}</Typography>

      <Circle sx={{ fontSize: '6px' }} />

      <Tooltip title={humanizeTimestamp(timestamp)}>
        <Typography>{formatDistanceToNow(timestamp, { addSuffix: true })}</Typography>
      </Tooltip>
    </Stack>
  )
}

interface AlertNotificationMenuItemProps extends Omit<MenuItemProps, 'children'> {
  notification: Notification<PrometheusAlert>
  onMute?: (notification: Notification<PrometheusAlert>, time?: MuteInterval) => void
}

function AlertNotificationMenuItem({
  notification,
  onMute,
  ...props
}: AlertNotificationMenuItemProps) {
  /**
   * The translation function.
   */
  const { t } = useTranslation('common')

  /**
   * The navigate function.
   */
  const navigate = useNavigate()

  /**
   * The device.
   */
  const [device, setDevice] = useState<Device>()

  /**
   * The ruuvitag.
   */
  const [ruuviTag, setRuuviTag] = useState<RuuviTag>()

  /**
   * The name of the device.
   */
  const deviceName = notification.body.data.labels?.device

  /**
   * The ID of the ruuvitag.
   */
  const ruuviTagId = notification.body.data.labels?.ruuvitag

  /**
   * Handle the "click" event.
   */
  function handleClick() {
    navigate(`/devices/${deviceName}/alerts`)
  }

  /**
   * Handle the "mute" event.
   */
  function handleMute(time?: MuteInterval) {
    onMute?.(notification, time)
  }

  /**
   * Load the device.
   */
  async function loadDevice(name: string, signal: AbortSignal) {
    setDevice(await getDevice(name, { signal }))
  }

  /**
   * Load the ruuvitag.
   */
  async function loadRuuviTag(id: string, signal: AbortSignal) {
    setRuuviTag(await getRuuviTag(id, { signal }))
  }

  /**
   * Load the device when the device name changes.
   */
  useEffect(() => {
    if (deviceName) {
      const controller = new AbortController()
      loadDevice(deviceName, controller.signal)
      return () => controller.abort()
    }
  }, [deviceName])

  /**
   * Load the ruuvitag when the ID changes.
   */
  useEffect(() => {
    if (ruuviTagId) {
      const controller = new AbortController()
      loadRuuviTag(ruuviTagId, controller.signal)
      return () => controller.abort()
    }
  }, [ruuviTagId])

  return (
    <MenuItem {...props} onClick={handleClick}>
      <ListItemIcon>
        <Warning color="warning" />
      </ListItemIcon>
      <ListItemText
        primary={
          <NotificationMenuItemTextPrimary
            title={
              device
                ? t('alertFromDevice', {
                    device: device?.asset?.name ?? device.name ?? deviceName
                  })
                : ruuviTag &&
                  t('alertFromRuuviTag', {
                    ruuviTag: ruuviTag?.asset?.name ?? ruuviTag.id
                  })
            }
            action={
              <Stack direction="row" gap={1}>
                <Button
                  variant="text"
                  color="warning"
                  size="small"
                  component={Link}
                  to={
                    device
                      ? `/devices/${deviceName}/alerts`
                      : ruuviTag
                        ? `/ruuvitags/${ruuviTag.id}/alerts`
                        : '/'
                  }
                >
                  {t('show')}
                </Button>

                <MuteButton onMute={handleMute} />
              </Stack>
            }
          />
        }
        primaryTypographyProps={{
          component: 'div'
        }}
        secondary={<NotificationMenuItemTextSecondary notification={notification} />}
        secondaryTypographyProps={{
          component: 'div',
          sx: {
            textWrap: 'nowrap',
            textOverflow: 'ellipsis',
            overflow: 'hidden'
          }
        }}
      />
    </MenuItem>
  )
}

interface EntityNotificationMenuItemProps extends Omit<MenuItemProps, 'children'> {
  notification: Notification<NotificationEntity>
  onDismiss?: (notification: Notification<NotificationEntity>) => void
}

function EntityNotificationMenuItem({ notification, ...props }: EntityNotificationMenuItemProps) {
  switch (notification.action) {
    case EntityEventAction.InsertProjectMembership:
    case EntityEventAction.UpdateProjectMembership:
      return (
        <ProjectMembershipNotificationMenuItem
          notification={notification as Notification<ProjectMembership>}
          {...props}
        />
      )
    case EntityEventAction.InsertUser:
      return (
        <UserNotificationMenuItem notification={notification as Notification<User>} {...props} />
      )
    default:
      return <></>
  }
}

interface ProjectMembershipNotificationMenuItemProps extends Omit<MenuItemProps, 'children'> {
  notification: Notification<ProjectMembership>
  onDismiss?: (notification: Notification<ProjectMembership>) => void
}

function ProjectMembershipNotificationMenuItem({
  notification,
  onDismiss: _,
  ...props
}: ProjectMembershipNotificationMenuItemProps) {
  // TODO: Load and provide the associated member and project.

  switch (notification.action) {
    case EntityEventAction.InsertProjectMembership:
      return <InsertProjectMembershipNotificationMenuItem notification={notification} {...props} />
    case EntityEventAction.UpdateProjectMembership:
      return <UpdateProjectMembershipNotificationMenuItem notification={notification} {...props} />
    default:
      return <></>
  }
}

function InsertProjectMembershipNotificationMenuItem({
  notification,
  onDismiss,
  ...props
}: ProjectMembershipNotificationMenuItemProps) {
  /**
   * The translation function.
   */
  const { t } = useTranslation('common')

  /**
   * The navigate function.
   */
  const navigate = useNavigate()

  /**
   * The project memebership.
   */
  const projectMembership = notification.body.data

  /**
   * The name of the member.
   */
  const name = projectMembership?.member
    ? `${projectMembership?.member?.firstName} ${projectMembership?.member?.lastName}`
    : t('user')

  /**
   * Handle the "click" action.
   */
  function handleClick() {
    navigate(`/projects/${projectMembership.projectId}#users`)
  }

  /**
   * Handle the "dismiss" action.
   */
  function handleDismiss() {
    onDismiss?.(notification)
  }

  return (
    <MenuItem {...props} onClick={handleClick}>
      <ListItemIcon>
        <Info color="primary" />
      </ListItemIcon>
      <ListItemText
        primary={
          <>
            {projectMembership?.member && (
              <NotificationMenuItemTextPrimary
                title={t('projectMembershipWasCreated', {
                  name,
                  role: projectMembership.role
                })}
                action={
                  <Stack direction="row" gap={2}>
                    <Button
                      variant="contained"
                      color="primary"
                      size="small"
                      component={Link}
                      to={`/projects/${projectMembership.projectId}#users`}
                    >
                      {t('show')}
                    </Button>

                    <Button variant="text" color="inherit" size="small" onClick={handleDismiss}>
                      {t('hide')}
                    </Button>
                  </Stack>
                }
              />
            )}
          </>
        }
        primaryTypographyProps={{
          component: 'div'
        }}
        secondary={<NotificationMenuItemTextSecondary notification={notification} />}
        secondaryTypographyProps={{
          component: 'div',
          sx: {
            textWrap: 'nowrap',
            textOverflow: 'ellipsis',
            overflow: 'hidden'
          }
        }}
      />
    </MenuItem>
  )
}

function UpdateProjectMembershipNotificationMenuItem({
  notification,
  onDismiss,
  ...props
}: ProjectMembershipNotificationMenuItemProps) {
  /**
   * The translation function.
   */
  const { t } = useTranslation('common')

  /**
   * The navigate function.
   */
  const navigate = useNavigate()

  /**
   * The project membership.
   */
  const projectMembership = notification.body.data

  /**
   * The name of the member.
   */
  const name = projectMembership?.member
    ? `${projectMembership?.member?.firstName} ${projectMembership?.member?.lastName}`
    : t('user')

  /**
   * Handle the "click" action.
   */
  function handleClick() {
    navigate(`/projects/${notification.body.data.projectId}#users`)
  }

  /**
   * Handle the "dismiss" action.
   */
  function handleDismiss() {
    onDismiss?.(notification)
  }

  return (
    <MenuItem {...props} onClick={handleClick}>
      <ListItemIcon>
        <Info color="primary" />
      </ListItemIcon>
      <ListItemText
        primary={
          <>
            {projectMembership?.member && (
              <NotificationMenuItemTextPrimary
                title={t('projectMembershipWasUpdated', {
                  name,
                  role: projectMembership.role
                })}
                action={
                  <Stack direction="row" gap={2}>
                    <Button
                      variant="contained"
                      size="small"
                      component={Link}
                      to={`/projects/${projectMembership.projectId}#users`}
                    >
                      {t('show')}
                    </Button>

                    <Button variant="text" color="inherit" size="small" onClick={handleDismiss}>
                      {t('hide')}
                    </Button>
                  </Stack>
                }
              />
            )}
          </>
        }
        primaryTypographyProps={{
          component: 'div'
        }}
        secondary={<NotificationMenuItemTextSecondary notification={notification} />}
        secondaryTypographyProps={{
          component: 'div',
          sx: {
            textWrap: 'nowrap',
            textOverflow: 'ellipsis',
            overflow: 'hidden'
          }
        }}
      />
    </MenuItem>
  )
}

interface UserNotificationMenuItemProps extends Omit<MenuItemProps, 'children'> {
  notification: Notification<User>
  onDismiss?: (notification: Notification<User>) => void
}

function UserNotificationMenuItem({ notification, ...props }: UserNotificationMenuItemProps) {
  switch (notification.action) {
    case EntityEventAction.InsertUser:
      return <InsertUserNotificationMenuItem notification={notification} {...props} />
    default:
      return <></>
  }
}

function InsertUserNotificationMenuItem({
  notification,
  onDismiss,
  ...props
}: UserNotificationMenuItemProps) {
  /**
   * The translation function.
   */
  const { t } = useTranslation('common')

  /**
   * The navigate function.
   */
  const navigate = useNavigate()

  /**
   * The user.
   */
  const user = notification.body.data

  /**
   * The fullname of the user.
   */
  const fullName = `${user?.firstName} ${user?.lastName}`

  /**
   * Handle the "click" action.
   */
  function handleClick() {
    navigate(`/users/${user.id}`)
  }

  /**
   * Handle the "dismiss" action.
   */
  function handleDismiss() {
    onDismiss?.(notification)
  }

  return (
    <MenuItem {...props} onClick={handleClick}>
      <ListItemIcon>
        <Info color="primary" />
      </ListItemIcon>
      <ListItemText
        primary={
          <NotificationMenuItemTextPrimary
            title={t('userWasCreated', { user: fullName })}
            action={
              <Stack direction="row" gap={2}>
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  component={Link}
                  to={`/users/${user.id}`}
                >
                  {t('show')}
                </Button>

                <Button variant="text" color="inherit" size="small" onClick={handleDismiss}>
                  {t('hide')}
                </Button>
              </Stack>
            }
          />
        }
        primaryTypographyProps={{
          component: 'div'
        }}
        secondary={<NotificationMenuItemTextSecondary notification={notification} />}
        secondaryTypographyProps={{
          component: 'div',
          sx: {
            textWrap: 'nowrap',
            textOverflow: 'ellipsis',
            overflow: 'hidden'
          }
        }}
      />
    </MenuItem>
  )
}
interface NotificationMenuItemProps extends Omit<MenuItemProps, 'children'> {
  notification: Notification<NotificationEntity | PrometheusAlert>
  onDismiss?: (notification: Notification<NotificationEntity | PrometheusAlert>) => void
  onMute?: (notification: Notification<PrometheusAlert>, time?: MuteInterval) => void
}

function NotificationMenuItem({
  notification,
  onDismiss,
  onMute,
  ...props
}: NotificationMenuItemProps) {
  switch (notification.eventType) {
    case EventType.AlertEvent:
      return (
        <AlertNotificationMenuItem
          notification={notification as Notification<PrometheusAlert>}
          onMute={onMute}
          {...props}
        />
      )
    case EventType.EntityEvent:
      return (
        <EntityNotificationMenuItem
          notification={notification as Notification<NotificationEntity>}
          onDismiss={onDismiss}
          {...props}
        />
      )
    default:
      return <></>
  }
}

export default function NavbarNotificationsDropdown() {
  /**
   * The translation function.
   */
  const { t } = useTranslation('common')

  /**
   * The current user.
   */
  const { currentUser } = useAuth()

  /**
   * The user alert rules.
   */
  const [userAlertRules, setUserAlertRules] = useState<UserAlertRule[]>([])

  /**
   * The menu context.
   */
  const { isMenuOpen, openMenu, closeMenu, anchorEl } = useMenu()

  /**
   * The user policy.
   */
  const { isSuperadmin, isAdmin } = usePolicy()

  /**
   * Indicates if the MFA notification is dismissed.
   */
  const isMfaNotificationDismissed = JSON.parse(
    window.localStorage.getItem('dismissMfaNotification') ?? 'false'
  )

  const showMfaNotification = !isMfaNotificationDismissed
  // && currentUser?.mfaType === MFAType.NONE

  /**
   * Indicates if the VAT notification is dismissed.
   */
  const isVatNotificationDismissed = JSON.parse(
    window.localStorage.getItem('dismissVatNotification') ?? 'false'
  )

  const showVatNotification = !isVatNotificationDismissed && (isSuperadmin || isAdmin)
  // && currentUser?.company?.vatNumber === null

  /**
   * Get the initial notifications.
   */
  const getInitialNotifications = () => {
    let notifications = JSON.parse(
      window.localStorage.getItem('notifications') ?? '[]'
    ) as Notification<NotificationEntity | PrometheusAlert>[]

    notifications = sortNotificationsByDate(notifications).filter(
      (notification) => !isActionRequestNotification(notification)
    )

    return notifications
  }

  /**
   * Get the initial muted alerts.
   */
  const getInitialMutedAlerts = (): Record<string, string> => {
    try {
      const parsed = JSON.parse(window.localStorage.getItem('mutedAlerts') ?? '{}')
      if (parsed !== null && typeof parsed === 'object' && !Array.isArray(parsed)) {
        return parsed as Record<string, string>
      }
    } catch {
      // ignore invalid JSON
    }
    return {}
  }

  /**
   * The company requests.
   */
  const [companyRequests, setCompanyRequests] = useState<ActionRequest[]>([])

  /**
   * The project requests.
   */
  const [projectRequests, setProjectRequests] = useState<ActionRequest[]>([])

  /**
   * The device requests.
   */
  const [deviceRequests, setDeviceRequests] = useState<ActionRequest[]>([])

  /**
   * The dismissed action request.
   */
  const dismissedActionRequests = useDismissedActionRequests()

  /**
   * Sort action requests by date.
   */
  const sortActionRequestsByDate = (actionRequests: ActionRequest[]) => {
    return actionRequests.sort((a, b) => {
      const aDate = new Date(a.createdAt)
      const bDate = new Date(b.createdAt)

      // Sort in descending order.
      return bDate.getTime() - aDate.getTime()
    })
  }

  /**
   * The action request actions that are ignored.
   * These actions will not be displayed in the action requests dropdown.
   */
  const ignoredActionRequestActions: (null | ActionRequestAction)[] = []

  /**
   * Check if the action request is dismissed.
   */
  const isActionRequestDismissed = (actionRequest: ActionRequest) => {
    return (
      dismissedActionRequests.includes(actionRequest.id) ||
      ignoredActionRequestActions.includes(actionRequest.action)
    )
  }

  /**
   * The action requests.
   */
  const actionRequests = sortActionRequestsByDate([
    ...companyRequests,
    ...projectRequests,
    ...deviceRequests
  ]).filter((actionRequest) => !isActionRequestDismissed(actionRequest))

  /**
   * The notification messages.
   */
  const [notifications, setNotifications] =
    useState<Notification<NotificationEntity | PrometheusAlert>[]>(getInitialNotifications())

  /**
   * The record muted alerts where the key is the device name and the value is the date when the mute ends.
   */
  const [mutedAlerts, setMutedAlerts] = useState<Record<string, string>>(getInitialMutedAlerts())

  /**
   * Get the alert notifications.
   */
  const alertNotifications = notifications.filter(
    (notification) => notification.eventType === EventType.AlertEvent
  ) as Notification<PrometheusAlert>[]

  /**
   * Add a new notification.
   */
  const addNotification = (
    notification: Notification<NotificationEntity | PrometheusAlert>,
    persist = true
  ) => {
    let newNotifications = [...notifications]
    newNotifications.push(notification)

    newNotifications = sortNotificationsByDate(newNotifications)

    setNotifications(newNotifications)

    if (persist) {
      window.localStorage.setItem('notifications', JSON.stringify(newNotifications))
    }
  }

  /**
   * Remove the notification.
   */
  const removeNotification = (notification: Notification<NotificationEntity | PrometheusAlert>) => {
    const newNotifications = notifications.filter((n) => n.id !== notification.id)

    setNotifications(newNotifications)
    window.localStorage.setItem('notifications', JSON.stringify(newNotifications))
  }

  /**
   * Add a new action request.
   */
  const addActionRequest = (actionRequest: ActionRequest) => {
    const actionRequestExists = actionRequests.some((a) => a.id === actionRequest.id)

    if (!actionRequestExists) {
      if (isProjectActionRequest(actionRequest)) {
        setProjectRequests((prev) => [...prev, actionRequest])
      } else if (isCompanyActionRequest(actionRequest)) {
        setCompanyRequests((prev) => [...prev, actionRequest])
      } else if (isDeviceActionRequest(actionRequest)) {
        setDeviceRequests((prev) => [...prev, actionRequest])
      } else if (isUserActionRequest(actionRequest)) {
        // setUserRequests((prev) => [...prev, actionRequest])
      }
    }
  }

  /**
   * Remove an action request.
   */
  const removeActionRequest = (actionRequest: ActionRequest) => {
    if (isProjectActionRequest(actionRequest)) {
      setProjectRequests((prev) => prev.filter((a) => a.id !== actionRequest.id))
    } else if (isCompanyActionRequest(actionRequest)) {
      setCompanyRequests((prev) => prev.filter((a) => a.id !== actionRequest.id))
    } else if (isDeviceActionRequest(actionRequest)) {
      setDeviceRequests((prev) => prev.filter((a) => a.id !== actionRequest.id))
    } else if (isUserActionRequest(actionRequest)) {
      // setUserRequests((prev) =>
      //   prev.filter((a) => a.id !== actionRequest.id)
      // )
    }
  }

  /**
   * Remove expired notifications.
   */
  const removeExpiredNotifications = () => {
    const twentyFourHoursAgo = subHours(new Date(), 24)

    const newNotifications = notifications.filter(
      (notification) => new Date(notification.timestamp) > twentyFourHoursAgo
    )

    setNotifications(newNotifications)
    window.localStorage.setItem('notifications', JSON.stringify(newNotifications))
  }

  /**
   * Remove the expired muted alerts.
   */
  const removeExpiredMutedAlerts = () => {
    const now = new Date()

    const newMutedAlerts = Object.fromEntries(
      Object.entries(mutedAlerts).filter(([_, dateStr]) => new Date(dateStr) > now)
    )

    setMutedAlerts(newMutedAlerts)
    window.localStorage.setItem('mutedAlerts', JSON.stringify(newMutedAlerts))
  }

  /**
   * Mute the alert notification.
   */
  const muteAlertNotification = (
    notification: Notification<PrometheusAlert>,
    time?: MuteInterval
  ) => {
    const newMutedAlerts = { ...mutedAlerts }

    let muteEndDate = new Date()

    if (time === '1h') {
      muteEndDate = addHours(muteEndDate, 1)
    } else if (time === '1d') {
      muteEndDate = addDays(muteEndDate, 1)
    } else {
      muteEndDate = addYears(muteEndDate, 1)
    }

    const key = notification.body.data.labels?.device ?? notification.body.data.labels?.ruuvitag

    if (key) {
      newMutedAlerts[key] = muteEndDate.toISOString()

      // Set the muted notifications to state.
      setMutedAlerts(newMutedAlerts)
      window.localStorage.setItem('mutedAlerts', JSON.stringify(newMutedAlerts))
    }
  }

  /**
   * Handle the alert notification.
   */
  const handleAlertNotification = async (notification: Notification<PrometheusAlert>) => {
    // The alert.
    const alert = notification.body.data as PrometheusAlert

    // Get the device name.
    const deviceName = alert.labels.device
    const ruuviTagId = alert.labels.ruuvitag

    // Check if an alert notification for a device already exists.
    const alertExists = alertNotifications.some(
      (alertNotification) =>
        (deviceName && alertNotification.body.data.labels.device === deviceName) ||
        (ruuviTagId && alertNotification.body.data.labels.ruuvitag === ruuviTagId)
    )

    const isAlertFiring = alert.status === AlertStatus.FIRING

    const key = deviceName ?? ruuviTagId
    if (key) {
      const alertIsMuted = Object.keys(mutedAlerts).includes(key)

      let muteHasExpired = true
      if (mutedAlerts[key] !== undefined) {
        // Check if the alert is muted and the mute has expired.
        const muteEndDate = new Date(mutedAlerts[key])
        muteHasExpired = muteEndDate < new Date()
      }

      if (isAlertFiring && !alertExists && (!alertIsMuted || muteHasExpired)) {
        // Get the device or ruuvitag so that we can resolve project ID.
        let device: Device | undefined = undefined
        let ruuviTag: RuuviTag | undefined = undefined

        if (deviceName) {
          device = await getDevice(deviceName)
        } else if (ruuviTagId) {
          ruuviTag = await getRuuviTag(ruuviTagId)
        }

        if (device === undefined && ruuviTag === undefined) {
          return
        }

        const asset = device?.asset ?? ruuviTag?.asset

        if (asset?.projectId) {
          const projectId = asset.projectId

          // Find the alert rule by project and name.
          let alertRule: UserAlertRule | undefined = undefined

          if (device) {
            alertRule = userAlertRules.find(
              (alertRule) =>
                alertRule.projectId === projectId &&
                alertRule.alertRuleName === alert.labels.alertname
            )
          } else if (ruuviTag) {
            alertRule = userAlertRules.find(
              (alertRule) =>
                alertRule.ruuviTagId === ruuviTag.id &&
                alertRule.alertRuleName === alert.labels.alertname
            )
          }

          // Check if user has enabled app notifications.
          const appNotificationsAreEnabledForUser = Boolean(currentUser?.notificationsInApp)

          // Check if user has enabled app notifications for membership.
          const appNotificationsAreEnabledForProjectMembership = Boolean(
            currentUser?.projects?.some(
              (membership) =>
                membership.projectId === projectId && membership.showInAppNotifications
            )
          )

          // Check if the current user has enabled app notifcations for alert rule.
          const appNotificationsAreEnabledForUserAlertRule = Boolean(
            alertRule?.appNotificationEnabled
          )

          if (
            appNotificationsAreEnabledForUser &&
            appNotificationsAreEnabledForProjectMembership &&
            appNotificationsAreEnabledForUserAlertRule
          ) {
            addNotification(notification)
          }
        }
      }
    }
  }

  /**
   * Handle the action request notification.
   */
  const handleActionRequestNotification = (notification: Notification<ActionRequest>) => {
    const actionRequest = notification.body.data

    if (
      notification.action === EntityEventAction.RemoveActionRequest ||
      notification.action === EntityEventAction.SoftRemoveActionRequest ||
      (notification.action === EntityEventAction.UpdateActionRequest &&
        isActionRequestClosed(actionRequest))
    ) {
      // Remove the action request from notifications in case it has been stored.
      removeNotification(notification)

      removeActionRequest(actionRequest)
    } else if (notification.action === EntityEventAction.InsertActionRequest) {
      // Add as a new action request.
      addActionRequest(actionRequest)
    }
  }

  /**
   * Handle the project membership notification.
   */
  const handleProjectMembershipNotification = (notification: Notification<ProjectMembership>) => {
    if (
      notification.action === EntityEventAction.InsertProjectMembership ||
      notification.action === EntityEventAction.UpdateProjectMembership
    ) {
      addNotification(notification)
    }
  }

  /**
   * Handle the user notification.
   */
  const handleUserNotification = (notification: Notification<User>) => {
    if (notification.action === EntityEventAction.InsertUser) {
      addNotification(notification)
    }
  }

  /**
   * Handle the entity notification.
   */
  const handleEntityNotification = (notification: Notification<NotificationEntity>) => {
    if (isActionRequestNotification(notification)) {
      handleActionRequestNotification(notification as Notification<ActionRequest>)
    } else if (isProjectMembershipNotification(notification)) {
      handleProjectMembershipNotification(notification as Notification<ProjectMembership>)
    } else if (isUserNotification(notification)) {
      handleUserNotification(notification as Notification<User>)
    }
  }

  /**
   * Handle the next message.
   */
  const handleNotification = (notification: Notification<NotificationEntity | PrometheusAlert>) => {
    if (notification !== undefined) {
      // Check if the notification already exists.
      const notificationExists = notifications.some((n) => n.id === notification.id)

      if (!notificationExists) {
        if (isAlertNotification(notification)) {
          handleAlertNotification(notification as Notification<PrometheusAlert>)
        } else if (isEntityNotification(notification)) {
          handleEntityNotification(notification as Notification<NotificationEntity>)
        }
      }
    }
  }

  /**
   * Handle the "dismiss" action.
   */
  const handleDismiss = (notification: Notification<NotificationEntity | PrometheusAlert>) => {
    removeNotification(notification)
  }

  /**
   * Handle the "mute" event.
   */
  const handleMute = (notification: Notification<PrometheusAlert>, time?: MuteInterval) => {
    // Mute the notification.
    muteAlertNotification(notification, time)

    // Remove the notification.
    removeNotification(notification)
  }

  /**
   * Check if the given item is an action request.
   */
  const isActionRequestItem = (
    item: ActionRequest | Notification<NotificationEntity | PrometheusAlert>
  ) => {
    return 'requesterType' in item
  }

  /**
   * Check if the given item is a notification.
   */
  const isNotificationItem = (
    item: ActionRequest | Notification<NotificationEntity | PrometheusAlert>
  ) => {
    return 'eventType' in item
  }

  /**
   * Get the date of the item.
   */
  const getItemDate = (
    item: ActionRequest | Notification<NotificationEntity | PrometheusAlert>
  ) => {
    return isNotificationItem(item)
      ? new Date((item as Notification<NotificationEntity | PrometheusAlert>).timestamp)
      : isActionRequestItem(item)
        ? new Date((item as ActionRequest).createdAt)
        : null
  }

  /**
   * Sort items by date.
   */
  const sortItemsByDate = (
    items: Array<ActionRequest | Notification<NotificationEntity | PrometheusAlert>>
  ) => {
    return items.sort((a, b) => {
      const aDate = getItemDate(a)
      const bDate = getItemDate(b)

      // Sort items in descending order.
      if (aDate && bDate) {
        return bDate.getTime() - aDate.getTime()
      } else if (aDate) {
        return -1
      } else if (bDate) {
        return 1
      } else {
        return 0
      }
    })
  }

  /**
   * The sorted items.
   */
  const items = sortItemsByDate([...actionRequests, ...notifications])

  const notificationCount =
    (showMfaNotification ? 1 : 0) + (showVatNotification ? 1 : 0) + items.length

  const hasNotifications = notificationCount > 0

  /**
   * Get the company requests.
   */
  const getCompanyRequests = (signal: AbortSignal) => {
    if (!currentUser?.companyId) {
      return Promise.resolve([])
    }

    return getRequests(
      {
        requestableType: RequestableType.COMPANY,
        requestableId: currentUser.companyId.toString(),
        status: RequestStatus.REQUESTED
      },
      {
        signal
      }
    )
  }

  /**
   * Get the project requests.
   */
  const getProjectRequest = (signal: AbortSignal) => {
    if (!currentUser?.activeProjectMembership?.projectId) {
      return Promise.resolve([])
    }

    return getRequests(
      {
        requestableType: RequestableType.PROJECT,
        requestableId: currentUser.activeProjectMembership.projectId.toString(),
        status: RequestStatus.REQUESTED
      },
      {
        signal
      }
    )
  }

  /**
   * Get the device requests.
   */
  const getDeviceRequests = async (signal: AbortSignal) => {
    let deviceRequests = await getRequests(
      {
        requestableType: RequestableType.DEVICE,
        status: RequestStatus.REQUESTED
      },
      {
        signal
      }
    )

    // Filter devices that belong to the active project.
    deviceRequests = deviceRequests.filter((deviceRequest) => {
      return (
        (deviceRequest?.requestable as Device)?.asset?.projectId ===
        currentUser?.activeProjectMembership?.projectId
      )
    })

    return deviceRequests
  }

  /**
   * Load action requests.
   */
  const loadActionRequests = async (signal: AbortSignal) => {
    const [companyRequests, projectRequests, deviceRequests] = await Promise.all([
      getCompanyRequests(signal),
      getProjectRequest(signal),
      getDeviceRequests(signal)
    ])

    setCompanyRequests(companyRequests)
    setProjectRequests(projectRequests)
    setDeviceRequests(deviceRequests)
  }

  /**
   * Load user alert rules.
   */
  const loadUserAlertRules = async (userId: number, signal: AbortSignal) => {
    setUserAlertRules(
      await getUserAlertRules(
        userId,
        {},
        {
          signal
        }
      )
    )
  }

  /**
   * Load action requests when the component loads.
   */
  useEffect(() => {
    const controller = new AbortController()

    loadActionRequests(controller.signal)

    return () => controller.abort()
  }, [])

  /**
   * Subscribe notifications.
   */
  subscribeNotifications(handleNotification)

  /**
   * Load user alert rules when the ID of the current user changes.
   */
  useEffect(() => {
    if (currentUser?.id) {
      const controller = new AbortController()

      // FIXME: We should reload when user alert rules are updated.
      loadUserAlertRules(currentUser.id, controller.signal)

      return () => controller.abort()
    }
  }, [currentUser?.id])

  /**
   * Auto-cleanup notifications and muted alerts that have expired.
   */
  useEffect(() => {
    const ONE_MINUTE = 1000 * 60
    const interval = setInterval(() => {
      removeExpiredNotifications()
      removeExpiredMutedAlerts()
    }, ONE_MINUTE)
    return () => clearInterval(interval)
  }, [notifications, mutedAlerts])

  return (
    <React.Fragment>
      <Tooltip title={t('notifications')}>
        <IconButton color="inherit" onClick={openMenu} size="large">
          <Indicator badgeContent={notificationCount}>
            <NotificationsNone fontSize="inherit" />
          </Indicator>
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={isMenuOpen}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
        PaperProps={{
          sx: {
            width: 360
          }
        }}
        onClose={closeMenu}
      >
        {currentUser && !currentUser.notificationsInApp ? (
          <Box p={2}>
            <Typography variant="subtitle1" color="textPrimary" textAlign="center">
              {t('inAppNotificationsAreDisabled')}
            </Typography>
          </Box>
        ) : !hasNotifications ? (
          <Box p={2}>
            <Typography variant="subtitle1" color="textPrimary" textAlign="center">
              {t('youHaveNoNotifications')}
            </Typography>
          </Box>
        ) : (
          <List>
            {showMfaNotification && <MfaNotification key="mfa-notification" />}

            {showVatNotification && <VatNotification key="vat-notification" />}

            {items.map((item) =>
              isNotificationItem(item) ? (
                <NotificationMenuItem
                  key={item.id}
                  notification={item as Notification<NotificationEntity | PrometheusAlert>}
                  onDismiss={handleDismiss}
                  onMute={handleMute}
                />
              ) : (
                isActionRequestItem(item) && (
                  <ActionRequestNotificationMenuItem
                    key={item.id}
                    actionRequest={item as ActionRequest}
                    onClick={closeMenu}
                  />
                )
              )
            )}
          </List>
        )}
      </Menu>
    </React.Fragment>
  )
}
